import React, { useContext, useEffect, useState } from 'react';
import { CardBody } from 'reactstrap';
import echarts from 'echarts/lib/echarts';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { getPosition, getGrays, rgbaColor } from '../../../helpers/utils';
import FalconCardHeader from '../../common/FalconCardHeader';
import AppContext from '../../../context/Context';
import emptyState from "../../../assets/img/illustrations/graphplaceholder.png";

import 'echarts/lib/chart/line';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/title';

const getOption = (xAxisData, yAxisData, isDark, dataName, renewalSubscription, newSubscription, customColors, showXAxisLabels, viewportWidth) => {
    const grays = getGrays(true);
    const isTabletWidth = viewportWidth <= 991;
    const isMobileWidth = viewportWidth <= 576;
    const xAxisLabelInterval = isMobileWidth
        ? (xAxisData?.length > 12 ? Math.max(1, Math.ceil(xAxisData.length / 6) - 1) : 'auto')
        : isTabletWidth
            ? (xAxisData?.length > 18 ? Math.max(1, Math.ceil(xAxisData.length / 8) - 1) : 'auto')
            : xAxisData?.length > 24
                ? Math.max(0, Math.ceil(xAxisData.length / 12) - 1)
                : 'auto';
    const formatXAxisLabel = (value, compact = false) => {
        if (!value || typeof value !== 'string') {
            return value;
        }

        if (!compact) {
            return value;
        }

        const [year, month, day] = value.split('-');
        if (year && month && day) {
            return `${month}-${day}`;
        }

        return value;
    };
    const formatNumber = (num) => {
        if (num >= 1e6) return (num / 1e6).toFixed(1).replace(/\.0$/, "") + "m";
        if (num >= 1e3) return (num / 1e3).toFixed(1).replace(/\.0$/, "") + "k";
        return num;
    };

    const defaultColors = [
        "#1991FF", 
        "#1280FF", 
        "#00C853", 
        "#FF4500", 
        "#1E90FF"
    ];

    const colors = customColors || defaultColors;
    let data = [dataName];
    
    const getRgbaColor = (color) => {
        if (color.startsWith('#')) {
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, 1)`;
        }
        return color;
    };
    
    // First series using the first color from defaultColors
    const mainColor = colors[0];
    const mainRgba = getRgbaColor(mainColor);
    const lightColor = mainRgba.replace('1)', '0.1)');
    
    let series = [{
        type: 'line',
        data: yAxisData.map(element => {
            if (Number.isInteger(Number(element))) {
                return element;
            } else if (Number.isFinite(Number(element))) {
                return parseFloat(element).toFixed(2);
            }
            return element;
        }),
        lineStyle: {
            color: mainRgba,
            type: 'solid',
            width: 3
        },
        itemStyle: {
            color: mainRgba,
            borderColor: mainRgba,
            borderWidth: 2
        },
        symbol: 'circle',
        symbolSize: 6,
        showSymbol: false,
        smooth: false,
        hoverAnimation: true,
        name: dataName,
        areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: mainRgba.replace('1)', '0.6)') },
                { offset: 1, color: lightColor }
            ])
        }
    }];

    if (renewalSubscription) {
        data.push('Renewal Subscription Revenue($)');
        // Use second color
        const secondaryColor = colors[1];
        const secondaryRgba = getRgbaColor(secondaryColor);
        const secondaryLightColor = secondaryRgba.replace('1)', '0.1)');
        
        series.push({
            type: 'line',
            data: renewalSubscription.map(element => {
                if (Number.isInteger(Number(element))) {
                    return element;
                } else if (Number.isFinite(Number(element))) {
                    return parseFloat(element).toFixed(2);
                }
                return element;
            }),
            lineStyle: {
                color: secondaryRgba,
                type: 'solid',
                width: 3
            },
            itemStyle: {
                color: secondaryRgba,
                borderColor: secondaryRgba,
                borderWidth: 2
            },
            symbol: 'circle',
            symbolSize: 6,
            showSymbol: false,
            smooth: false,
            hoverAnimation: true,
            name: 'Renewal Subscription Revenue($)',
            areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: secondaryRgba.replace('1)', '0.6)') },
                    { offset: 1, color: secondaryLightColor }
                ])
            }
        });
    }

    if (newSubscription) {
        data.push('New Subscription Revenue($)');
        // Use third color
        const tertiaryColor = colors[2];
        const tertiaryRgba = getRgbaColor(tertiaryColor);
        const tertiaryLightColor = tertiaryRgba.replace('1)', '0.1)');
        
        series.push({
            type: 'line',
            data: newSubscription.map(element => {
                if (Number.isInteger(Number(element))) {
                    return element;
                } else if (Number.isFinite(Number(element))) {
                    return parseFloat(element).toFixed(2);
                }
                return element;
            }),
            lineStyle: {
                color: tertiaryRgba,
                type: 'solid',
                width: 3
            },
            itemStyle: {
                color: tertiaryRgba,
                borderColor: tertiaryRgba,
                borderWidth: 2
            },
            symbol: 'circle',
            symbolSize: 6,
            showSymbol: false,
            smooth: false,
            hoverAnimation: true,
            name: 'New Subscription Revenue($)',
            areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: tertiaryRgba.replace('1)', '0.6)') },
                    { offset: 1, color: tertiaryLightColor }
                ])
            }
        });
    }

    const isCompactXAxis = isTabletWidth;

    return {
        tooltip: {
            trigger: 'axis',
            padding: [7, 10],
            backgroundColor: grays.white,
            borderColor: grays[100],
            borderWidth: 1,
            textStyle: { color: grays.black },
            transitionDuration: 0,
            position(pos, params, dom, rect, size) {
                return getPosition(pos, params, dom, rect, size);
            },
            axisPointer: {
                type: 'line',
                lineStyle: {
                    color: grays['300'],
                    type: 'solid',
                }
            }
        },
        xAxis: {
            type: 'category',
            data: xAxisData,
            boundaryGap: false,
            axisPointer: {
                lineStyle: {
                    color: grays['300'],
                    type: 'solid'
                }
            },
            splitLine: { show: false },
            axisLine: {
                lineStyle: {
                    color: rgbaColor('#000', 0.01),
                    type: 'solid'
                }
            },
            axisTick: { show: false },
            axisLabel: {
                show: showXAxisLabels,
                color: grays['400'],
                interval: xAxisLabelInterval,
                hideOverlap: true,
                showMaxLabel: true,
                margin: 12,
                rotate: isMobileWidth ? 45 : isTabletWidth ? 30 : 0,
                formatter: (value) => formatXAxisLabel(value, isCompactXAxis)
            }
        },
        yAxis: {
            type: 'value',
            axisPointer: { show: false },
            splitLine: {
                show: true,
                lineStyle: {
                    color: grays['900'],
                    type: 'solid'
                }
            },
            boundaryGap: false,
            axisLabel: {
                show: true,
                color: grays['400'],
                margin: 15,
                formatter: function (value) {
                    return formatNumber(value);
                }
            },
            axisTick: { show: false },
            axisLine: { show: false }
        },
        series,
        legend: {
            show: false
        },
        grid: {
            right: showXAxisLabels ? (isMobileWidth ? '24px' : isTabletWidth ? '32px' : '48px') : '0px',
            left: showXAxisLabels ? (isMobileWidth ? '40px' : isTabletWidth ? '44px' : '56px') : '44px',
            bottom: showXAxisLabels ? (isMobileWidth ? '24%' : isTabletWidth ? '20%' : '15%') : '15%',
            top: series.length > 1 ? '15%' : '10%',
            containLabel: true
        }
    };
};

const PointGraph = ({ xAxis=[0], yAxis=["Loading"], title, dataName, renewalSubscription, newSubscription, colors, showLoading=false, showXAxisLabels=false }) => {
    const { isDark } = useContext(AppContext);
    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1200
    );
    const hasData = xAxis && xAxis.length > 0 && yAxis && yAxis.length > 0;

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleResize = () => {
            setViewportWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    return (
        <>
            <FalconCardHeader title={title} light={false} titleTag="h6" className="pb-0" />
            <CardBody className="text-center w-100 p-3">
                {(!hasData && !showLoading) ? (
                    <div className="text-center p-5" style={{minHeight:'24rem'}}>
                        <img src={emptyState} alt="No data" style={{ width: '150px', height: '150px' }} />
                        <p>No data available</p>
                    </div>
                ) : (
                    <ReactEchartsCore
                        showLoading={showLoading} 
                        echarts={echarts} 
                        option={getOption(xAxis, yAxis, isDark, dataName, renewalSubscription, newSubscription, colors, showXAxisLabels, viewportWidth)} 
                        style={{ minHeight: '24rem', border: 'transparent' }} 
                    />
                )}
            </CardBody>
        </>
    );
};

export default PointGraph;
